import apiConfig from "../config/apiConfig";

// ─── Singleton refresh mutex ───────────────────────────────────────────────────
let refreshPromise: Promise<boolean> | null = null;
let isLoggingOut = false;

// ─── Token helper ──────────────────────────────────────────────────────────────
const getCookieValue = (name: string): string | null => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop()?.split(";").shift() || null;
    return null;
};

/** Cookie first, localStorage fallback */
const getAccessToken = (): string | null => {
    const cookieToken = getCookieValue("access_token");
    const localToken = localStorage.getItem("authToken");
    
    if (cookieToken) return cookieToken;
    if (localToken) return localToken;
    return null;
};

// ─── Session cleanup helper ────────────────────────────────────────────────────
const redirectToLogin = () => {
    if (isLoggingOut) return;
    isLoggingOut = true;

    try {
        // Best-effort backend logout (fire-and-forget)
        originalFetch(`${apiConfig.API_BASE_URL}/auth/logout`, {
            method: "POST",
            credentials: "include",
        }).catch(() => {});
    } catch {}

    localStorage.removeItem("authToken");
    localStorage.removeItem("user");
    localStorage.removeItem("isLoggedIn");

    if (window.location.pathname !== "/") {
        window.location.href = "/";
    }
};

// ─── Main fetch wrapper ────────────────────────────────────────────────────────
const originalFetch = window.fetch;

/**
 * Drop-in fetch replacement that globally intercepts fetch.
 *  1. Only applies to URLs matching our API base.
 *  2. Injects Authorization header with current access token (cookie or localStorage),
 *     overriding any stale explicit tokens.
 *  3. On 401 → calls POST /auth/refresh once (mutex-guarded).
 *  4. On refresh success → retries the original request with the new token.
 *  5. On refresh failure → clears session and redirects to login.
 */
export const apiFetch = async (
    input: RequestInfo | URL,
    init?: RequestInit
): Promise<Response> => {
    
    // Determine the URL string safely
    let urlString = "";
    if (typeof input === "string") {
        urlString = input;
    } else if (input instanceof URL) {
        urlString = input.href;
    } else if (input instanceof Request) {
        urlString = input.url;
    }

    const baseUrl = apiConfig.API_BASE_URL || "";
    const isOurApi = baseUrl !== "" && urlString.includes(baseUrl);

    // If it's an external API (like fetching countries payload), pass untouched
    if (!isOurApi) {
        return originalFetch(input, init);
    }

    const token = getAccessToken();

    // Prepare headers safely using the native Headers object
    const updatedHeaders = new Headers(init?.headers);

    // Default to application/json if not overriding something like FormData boundary
    if (!updatedHeaders.has("Content-Type") && !(init?.body instanceof FormData)) {
        updatedHeaders.set("Content-Type", "application/json");
    }

    const isPublicAuthEndpoint = urlString.includes("/auth/refresh") || 
                          urlString.includes("/auth/login") || 
                          urlString.includes("/auth/register") || 
                          urlString.includes("/auth/add_user") || 
                          urlString.includes("/auth/forgot-password") || 
                          urlString.includes("/auth/reset-password") ||
                          urlString.includes("/auth/send-otp") ||
                          urlString.includes("/auth/verify-otp") ||
                          urlString.includes("/auth/logout");

    // FORCE overwrite any explicitly passed stale Cookies.get("access_token") 
    // from legacy frontend files. DO NOT send token on public auth endpoints
    // to prevent Spring Security from rejecting login with 401 Expired Token.
    if (token && !isPublicAuthEndpoint) {
        updatedHeaders.set("Authorization", `Bearer ${token}`);
    } else {
        updatedHeaders.delete("Authorization");
    }

    const updatedInit: RequestInit = {
        ...init,
        headers: updatedHeaders,
        credentials: "include",
    };

    // console.log(`apiFetch: Calling ${urlString}`, updatedInit);
    let response;
    try {
        response = await originalFetch(input, updatedInit);
    } catch (error) {
        console.error(`apiFetch: Network error for ${urlString}`, error);
        throw error;
    }

    // Stop and return early if success, or not an auth issue, or if it was an auth endpoint handled specifically by its component
    const isAuthEndpoint = urlString.includes("/auth/refresh") || 
                          urlString.includes("/auth/login") || 
                          urlString.includes("/auth/register") || 
                          urlString.includes("/auth/add_user") || 
                          urlString.includes("/auth/forgot-password") || 
                          urlString.includes("/auth/reset-password");

    if (response.status !== 401 || isAuthEndpoint) {
        // console.log(`apiFetch: Response ${response.status} for ${urlString}`);
        return response;
    }

    console.warn(`apiFetch: Got 401 for ${urlString}. Attempting silent refresh...`);

    // ── ACCESS TOKEN EXPIRED — attempt silent refresh ─────────────────────────
    if (!refreshPromise) {
        refreshPromise = originalFetch(`${apiConfig.API_BASE_URL}/auth/refresh`, {
            method: "POST",
            credentials: "include",
        })
            .then((res) => res.ok)
            .catch(() => false)
            .finally(() => {
                refreshPromise = null; // release mutex after completion
            });
    }

    const refreshSuccess = await refreshPromise;

    if (refreshSuccess) {
        // Backend set a new access_token cookie
        const newToken = getAccessToken();
        if (newToken) {
            updatedHeaders.set("Authorization", `Bearer ${newToken}`);
            updatedInit.headers = updatedHeaders;
        }
        return originalFetch(input, updatedInit);
    }

    // ── REFRESH ALSO FAILED — session is dead ─────────────────────────────────
    redirectToLogin();
    
    // Return a never-resolving promise to stall the UI and prevent 
    // React Query / calling components from rendering a red error
    // screen while the browser redirects to login.
    return new Promise(() => {});
};

// Injection method to globally override generic fetch
export const setupFetchInterceptor = () => {
    window.fetch = apiFetch;
};